import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useApp } from "../components/appState";
import { Icon } from "../components/Icons";
import { Timeline } from "../components/Timeline";
import { yearSpan } from "../lib/format";
import { fetchBands, fetchVenues, type BandRow, type VenueRow } from "../lib/rosterPage";

type Sort = "count" | "name";
interface Item {
  kind: "band" | "venue";
  id: number;
  name: string;
  n: number;
  first: string | null;
  last: string | null;
  years: number[];
  sub: string;
  closed: boolean;
  hay: string;
}

const fromBand = (b: BandRow): Item => ({
  kind: "band", id: b.id, name: b.name, n: b.times_seen, first: b.first, last: b.last, years: b.years, closed: false,
  sub: [yearSpan(b.first, b.last), b.aliases.length ? `also as ${b.aliases.join(", ")}` : ""].filter(Boolean).join(" · "),
  hay: `${b.name} ${b.aliases.join(" ")}`.toLowerCase(),
});
const fromVenue = (v: VenueRow): Item => ({
  kind: "venue", id: v.id, name: v.name, n: v.show_count, first: v.first, last: v.last, years: v.years, closed: v.closed,
  sub: [v.location, yearSpan(v.first, v.last)].filter(Boolean).join(" · "),
  hay: `${v.name} ${v.location ?? ""}`.toLowerCase(),
});

/** A–Z ignores a leading "The", as the mockup sorts. */
const sortKey = (name: string) => name.replace(/^the\s+/i, "").toLowerCase();

export default function Roster({ tab }: { tab: "bands" | "venues" }) {
  const { open, version, firstYear } = useApp();
  const [sp, setSp] = useSearchParams();
  const sort: Sort = sp.get("sort") === "name" ? "name" : "count";
  const [q, setQ] = useState("");
  const [bands, setBands] = useState<BandRow[] | null>(null);
  const [venues, setVenues] = useState<VenueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([fetchBands("", "count"), fetchVenues("", "count")])
      .then(([b, v]) => {
        if (!live) return;
        setBands(b);
        setVenues(v);
      })
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [version]);

  const rows = useMemo(() => {
    const items = tab === "bands" ? (bands ?? []).map(fromBand) : (venues ?? []).map(fromVenue);
    const needle = q.trim().toLowerCase();
    const hit = needle ? items.filter((r) => r.hay.includes(needle)) : items;
    return sort === "name" ? [...hit].sort((a, b) => sortKey(a.name).localeCompare(sortKey(b.name))) : hit;
  }, [tab, bands, venues, q, sort]);

  const setSort = (s: Sort) => setSp((p) => (s === "name" ? p.set("sort", "name") : p.delete("sort"), p), { replace: true });
  return (
    <>
      <div className="ph">
        <h1>Bands &amp; venues</h1>
      </div>
      <div className="seg" role="group" aria-label="Bands or venues">
        <Link to={`/bands${sort === "name" ? "?sort=name" : ""}`} aria-pressed={tab === "bands"} className="segl" style={segStyle(tab === "bands")}>
          Bands <span className="mono faint">{bands?.length ?? ""}</span>
        </Link>
        <Link to={`/venues${sort === "name" ? "?sort=name" : ""}`} aria-pressed={tab === "venues"} className="segl" style={segStyle(tab === "venues")}>
          Venues <span className="mono faint">{venues?.length ?? ""}</span>
        </Link>
      </div>
      <div className="toolbar">
        <label className="search">
          <Icon name="search" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${tab}`} aria-label={`Search ${tab}`} />
        </label>
        <div className="chips">
          <button type="button" aria-pressed={sort === "count"} onClick={() => setSort("count")}>
            Most seen
          </button>
          <button type="button" aria-pressed={sort === "name"} onClick={() => setSort("name")}>
            A–Z
          </button>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {!bands && !error && <p className="loading">Loading…</p>}
      <div>
        {rows.map((r, i) => (
          <button type="button" key={`${r.kind}${r.id}`} className="rrow" onClick={() => open(r.kind, r.id)}>
            <span className="rk">{sort === "count" ? i + 1 : ""}</span>
            <span className="rn">
              <span className="t">{r.name}</span>
              {r.closed && <span className="chip closed">closed</span>}
            </span>
            <span className="rc">
              {r.n}
              <small>{r.n === 1 ? "TIME" : "TIMES"}</small>
            </span>
            <span className="rs">{r.sub}</span>
            <span style={{ gridColumn: 2 }}>
              <Timeline years={r.years} first={firstYear} />
            </span>
          </button>
        ))}
        {bands && !rows.length && <p className="muted">No match.</p>}
      </div>
    </>
  );
}

const segStyle = (on: boolean) => ({
  padding: "6px 12px",
  borderRadius: 7,
  fontSize: 13,
  textDecoration: "none",
  color: on ? "var(--text)" : "var(--muted)",
  background: on ? "var(--raised)" : "transparent",
  minHeight: 34,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});
